import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import messages from '../../lib/i18n';
import btnClose from '../../images/common/close.svg';
import { MODE_DIALOG } from '../../reducers/blackboard';
import { convertUnixtTimeToDateTime } from '../../lib/tools';
import { RequiredChip } from '../Parts/Chip';
import Textarea from '../../styles/common/Textarea';
import { MAX_LENGTH } from '../../lib/constants';
import ColorSF2 from '../../styles/common/ColorSF2';

const MainBlock = styled.div`
  width: 100vw;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;
const DialogBox = styled.div`
  width: 522px;
  background-color: ${ColorSF2.white};
`;
const FlexColumnBlock = styled.div`
  display: flex;
  flex-direction: column;
`;
const HeaderBlock = styled.div`
  height: 64px;
  display: flex;
  padding: 10px;
  justify-content: space-between;
  align-items: center;
  background-color: ${ColorSF2.paleBlue};
  font-size: 15px;
`;
const CloseButton = styled.div`
  margin: 20px 18px 20px 18px;
`;
const CloseButtonImage = styled.img`
  width: 20px;
  height: 20px;
`;
const BodyBlock = styled(FlexColumnBlock)`
  padding: 10px;
  align-items: center;
`;
const SubmitBlock = styled.div`
  margin: 10px 0 20px 0;
  display: flex;
  justify-content: center;
`;
const Button = styled.div`
  margin: 5px;
  width: 150px;
  height: 50px;
  display: ${props => (props.hidden ? 'none' : 'flex')};
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  border: 1px solid ${ColorSF2.mediumGray};
  background-color: ${ColorSF2.white};
  font-weight: bold;
  font-size: 15px;
  cursor: ${props => (props.disable ? 'default' : 'pointer')};
  opacity: ${props => (props.disable ? 0.5 : 1)};
  &:hover {
    opacity: 0.8;
  }
`;
const RegisterButton = styled(Button)`
  background-color: ${ColorSF2.navyBlue};
  color: ${ColorSF2.white};
  border: none;
`;
const DeleteButton = styled(Button)`
  background-color: ${ColorSF2.red};
  color: ${ColorSF2.white};
  border: none;
`;
const InputWrapper = styled.div`
  width: 100%;
  height: ${props => (props.height && `${props.height}px`) || '46px'};
  display: ${props => (props.hidden ? 'none' : 'flex')};
  align-items: center;
  background-color: ${ColorSF2.white};
  border-bottom: 1px solid ${ColorSF2.mediumGray};
`;
const InputTitle = styled.div`
  padding-left: 8px;
  width: 120px;
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 14px;
  background-color: ${ColorSF2.lightGray};
`;
const InputContents = styled.div`
  margin-left: 8px;
  display: flex;
  align-items: center;
`;
const InputAndValidation = styled(FlexColumnBlock)`
  position: relative;
`;
const InputArea = styled.input`
  width: 359px;
  height: 23px;
  padding: 2px;
  line-height: 20px;
  font-size: 12px;
  border: 1px solid ${ColorSF2.mediumGray};
`;
const TextareaAndValidation = styled(FlexColumnBlock)`
  background-color: #3a3e52;
  position: relative;
`;
const NoteTextarea = styled(Textarea)`
  width: 359px;
  height: 86px;
  border: 1px solid ${ColorSF2.mediumGray};
`;
const ErrorMessage = styled.p`
  position: absolute;
  top: 26px;
  font-size: 10px;
  line-height: 10px;
  color: ${props => props.isError && ColorSF2.red};
`;
const ErrorMessageNote = styled(ErrorMessage)`
  top: 90px;
`;
const Select = styled.select`
  width: 120px;
  height: 24px;
  border: 1px solid ${ColorSF2.mediumGray};
`;

const RegistrationDialog = ({
  locale,
  mode,
  blackboard,
  showError,
  close,
  edit,
  create,
  update,
  remove,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const isCreate = mode === MODE_DIALOG.create;
  const {
    id,
    title: currentTitle,
    subtitle: currentSubtitle,
    note: currentNote,
    titleSuffix,
    operatorName,
    createdAt,
  } = blackboard;
  const title = currentTitle || '';
  const subtitle = currentSubtitle || '';
  const note = currentNote || '';
  const createdDate =
    (createdAt && convertUnixtTimeToDateTime(createdAt)) || '';
  const trimTitle = title.replace(/\s+/g, '');
  const validateTitle = trimTitle.length > 0 && trimTitle.length < 256;
  const validateSubtitle = subtitle.length < 256;
  const validateNote = note.length < 256;
  const enabledCreate = validateTitle && validateSubtitle && validateNote;
  const enabledUpdate = validateTitle && validateSubtitle && validateNote;
  const errorMessageTotalWordCount = error =>
    (error &&
      formatMessage(
        { id: 'remotePage.bs.input.error.overTotalWordCount' },
        { max: 255 },
      )) ||
    '';
  const i18nMessages = messages[locale];
  const suffixes = i18nMessages['remotePage.bs.label.letters.suffixes'];
  const suffixSelections = () =>
    Object.entries(suffixes).map(params => {
      const [key, value] = params;
      return (
        <option value={key} key={`suffixSelections_${key}`}>
          {value}
        </option>
      );
    });

  return (
    <MainBlock id="closeRegistrationDialog" role="presentation">
      <DialogBox role="presentation" onClick={e => e.stopPropagation()}>
        <FlexColumnBlock>
          <HeaderBlock>
            {(isCreate && (
              <FormattedMessage id="blackboardList.registration.title.create" />
            )) || (
              <FormattedMessage id="blackboardList.registration.title.update" />
            )}
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={() => close()}
            >
              <CloseButtonImage src={btnClose} alt="close" />
            </CloseButton>
          </HeaderBlock>
          <BodyBlock>
            <InputWrapper hidden={isCreate}>
              <InputTitle>
                <FormattedMessage id="blackboardList.registration.item.createdAt" />
              </InputTitle>
              <InputContents>{createdDate}</InputContents>
            </InputWrapper>
            <InputWrapper hidden={isCreate}>
              <InputTitle>
                <FormattedMessage id="blackboardList.registration.item.operatorName" />
              </InputTitle>
              <InputContents>{operatorName}</InputContents>
            </InputWrapper>
            <InputWrapper>
              <InputTitle>
                <FormattedMessage id="blackboardList.registration.item.title" />
                <RequiredChip />
              </InputTitle>
              <InputContents>
                <InputAndValidation>
                  <InputArea
                    id="updateSendTextConstructionName"
                    type="text"
                    value={title}
                    onChange={e => edit({ title: e.target.value })}
                    maxLength={MAX_LENGTH.text}
                  />
                  <ErrorMessage isError={!validateTitle}>
                    {showError && errorMessageTotalWordCount(!validateTitle)}
                  </ErrorMessage>
                </InputAndValidation>
              </InputContents>
            </InputWrapper>
            <InputWrapper>
              <InputTitle>
                <FormattedMessage id="blackboardList.registration.item.titleSuffix" />
              </InputTitle>
              <InputContents>
                <InputAndValidation>
                  <Select
                    id="selectConstructionNameSuffix"
                    value={titleSuffix || ''}
                    onChange={e => {
                      edit({
                        titleSuffix:
                          e.target.value !== 'none' ? e.target.value : '',
                      });
                    }}
                  >
                    {suffixSelections()}
                  </Select>
                </InputAndValidation>
              </InputContents>
            </InputWrapper>
            <InputWrapper>
              <InputTitle>
                <FormattedMessage id="blackboardList.registration.item.subtitle" />
              </InputTitle>
              <InputContents>
                <InputAndValidation>
                  <InputArea
                    id="updateSendTextConstructionType"
                    type="text"
                    value={subtitle}
                    onChange={e => edit({ subtitle: e.target.value })}
                    maxLength={MAX_LENGTH.text}
                  />
                  <ErrorMessage isError={!validateSubtitle}>
                    {errorMessageTotalWordCount(!validateSubtitle)}
                  </ErrorMessage>
                </InputAndValidation>
              </InputContents>
            </InputWrapper>
            <InputWrapper height="118">
              <InputTitle>
                <FormattedMessage id="blackboardList.registration.item.note" />
              </InputTitle>
              <InputContents>
                <TextareaAndValidation>
                  <NoteTextarea
                    id="updateSendTextData"
                    type="text"
                    defaultValue={note}
                    onChange={e => edit({ note: e.target.value })}
                    maxLength={MAX_LENGTH.text}
                  />
                  <ErrorMessageNote isError={!validateNote}>
                    {errorMessageTotalWordCount(!validateNote)}
                  </ErrorMessageNote>
                </TextareaAndValidation>
              </InputContents>
            </InputWrapper>
          </BodyBlock>
          <SubmitBlock>
            <Button
              id="blackboardCancel"
              role="presentation"
              className="button"
              onClick={() => close()}
            >
              <FormattedMessage id="common.button.cancel" />
            </Button>
            <DeleteButton
              hidden={isCreate}
              id="blackboardDelete"
              role="presentation"
              className="button"
              onClick={() => remove(id)}
            >
              <FormattedMessage id="common.button.delete" />
            </DeleteButton>
            <RegisterButton
              disable={
                (isCreate && !enabledCreate) || (!isCreate && !enabledUpdate)
              }
              id="blackboardCreate"
              role="presentation"
              className="button"
              onClick={() => {
                if (isCreate && enabledCreate) {
                  create({ title, subtitle, titleSuffix, note });
                }
                if (!isCreate && enabledUpdate) {
                  update({ id, title, subtitle, titleSuffix, note });
                }
              }}
            >
              {(isCreate && <FormattedMessage id="common.button.create" />) || (
                <FormattedMessage id="common.button.update" />
              )}
            </RegisterButton>
          </SubmitBlock>
        </FlexColumnBlock>
      </DialogBox>
    </MainBlock>
  );
};

RegistrationDialog.propTypes = {
  locale: PropTypes.string.isRequired,
  mode: PropTypes.number.isRequired,
  blackboard: PropTypes.object.isRequired,
  showError: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  edit: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  remove: PropTypes.func.isRequired,
};

export default RegistrationDialog;
